<template>
    <tenancies-find
        :params="{ tenancy: $route.params.tenancy }"
        :include="[
            'documents(paginate:1,4|include:files(paginate:1,1))',
            'fees(paginate:1,5)',
            'inventories(paginate:1,4|include:files(paginate:1,1))',
            'property(include:address,landlord)',
            'rents(paginate:1,5)',
            'tasks(paginate:1,5)',
            ...($gate.role('landlord') ? [
                'note',
                'residencies(paginate:1,5|include:occupant(include:tenant))'
            ] : []),
            ...($gate.role('tenant') ? [
                'auto_payment(include:source)'
            ] : [])
        ]"
    >
        <div
            slot-scope="{
                sid,
                has_loaded,
                is_loading,
                data: tenancy,
                error,
                find
            }"
        >
            <template v-if="!has_loaded">
                <base-loading
                    :retry="find"
                    :error="error"
                    class="py-6"
                />

                <error-modal
                    v-if="error && $error.is(error, { exclude: [400, 422] })"
                    :error="error"
                />
            </template>

            <template v-else>
                <base-hint
                    v-if="$gate.role('landlord')"
                    name="landlords-tenancies-show"
                >
                    <template slot="heading">
                        Viewing a tenancy
                    </template>

                    <p class="mb-3">
                        You're currently viewing the tenancy for {{ tenancy.property.name }}.
                    </p>

                    <p class="mb-3">
                        As you'll see on this page - tenancies have tenants, rents, fees, documents and inventories associated with them.
                    </p>

                    <button
                        class="w-full btn btn-sm btn-grey-darker md:w-auto"
                        @click.prevent="introjs"
                    >
                        What's on this page?
                    </button>
                </base-hint>

                <base-hint
                    v-else-if="$gate.role('tenant')"
                    name="tenants-tenancies-show"
                >
                    <template slot="heading">
                        Viewing a tenancy
                    </template>

                    <p class="mb-3">
                        You're currently viewing the tenancy for {{ tenancy.property.name }}.
                    </p>

                    <p class="mb-3">
                        As you'll see on this page - properties have listings, tenancies and documents associated with them.
                    </p>

                    <button
                        class="w-full btn btn-sm btn-grey-darker md:w-auto"
                        @click.prevent="introjs"
                    >
                        What's on this page?
                    </button>
                </base-hint>

                <base-alert
                    v-if="tenancy.archived_at"
                    level="warning"
                    class="mb-6"
                >
                    <template slot="heading">Tenancy Archived</template>
                    <template slot="content">
                        <p>This tenancy was archived on {{ tenancy.archived_at | moment('D MMM, YYYY') }}.</p>
                    </template>
                </base-alert>

                <base-alert
                    v-else-if="tenancy.terminated_at"
                    level="warning"
                    class="mb-6"
                >
                    <template slot="heading">Tenancy Terminated</template>
                    <template slot="content">
                        <p>
                            This tenancy was terminated on {{ tenancy.terminated_at | moment('D MMM, YYYY') }}. You might
                            want to consider archiving it.
                        </p>
                    </template>
                </base-alert>

                <details-card class="mb-5 id-details-card">
                    <template slot="header">
                        <div class="flex-grow mr-3 overflow-hidden">
                            <h1 class="text-lg font-normal truncate">
                                Tenancy at {{ $gate.role('landlord') ? tenancy.property.name : tenancy.property.address.name }}
                            </h1>
                        </div>

                        <div
                            v-if="is_loading"
                            class="w-6 h-6 mr-3"
                        >
                            <base-svg
                                name="icon-loading"
                                class="w-full h-full"
                            />
                        </div>

                        <chats-store-button
                            v-if="$gate.role('tenant')"
                            :personas="[tenancy.property.landlord]"
                        >
                            Message Landlord
                        </chats-store-button>

                        <edit-dropdown
                            v-if="$gate.role('landlord') && !tenancy.archived_at"
                            :tenancy="tenancy"
                            class="flex-none id-edit-dropdown"
                            @success="find"
                        />
                    </template>
                    <template slot="body">
                        <grid-layout class="-mt-3">
                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Property</div>
                                <div class="text-sm">
                                    <template v-if="$gate.role('landlord')">
                                        <router-link
                                            :to="{
                                                name: $router.fix('dashboard.*.properties.show'),
                                                params: {
                                                    property: tenancy.property.uuid
                                                }
                                            }"
                                            class="no-underline text-blue"
                                        >
                                            {{ tenancy.property.name }}
                                        </router-link>
                                    </template>
                                    <template v-else>
                                            {{ tenancy.property.address.name }}
                                    </template>
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Period</div>
                                <div class="text-sm">
                                    <base-tenancy-period :tenancy="tenancy" />
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Duration</div>
                                <div class="text-sm">
                                    {{ tenancy.duration }} months
                                </div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Collection</div>
                                <div class="text-sm">{{ $_.upperFirst(tenancy.collection) }}</div>
                            </grid-layout-item>

                            <grid-layout-item class="mt-3 sm:w-1/2 lg:w-1/3">
                                <div class="text-xs tracking-wide uppercase text-grey-darker">Term</div>
                                <div class="text-sm">
                                    {{ $_.upperFirst(tenancy.term) }}, {{ tenancy.is_rolling ? 'rolling' : 'non-rolling' }}.
                                    <template v-if="tenancy.term !== tenancy.term_original">Originally {{ $_.upperFirst(tenancy.term_original) }}</template>
                                </div>
                            </grid-layout-item>
                        </grid-layout>
                    </template>
                </details-card>

                <grid-layout>
                    <grid-layout-item class="mb-6 md:w-1/3">
                        <div class="flex flex-col h-full bg-white rounded shadow id-balance-card">
                            <header class="flex items-center flex-none h-16 px-4 border-b sm:px-6">
                                <h3 class="flex-grow text-base font-normal">Balance</h3>
                            </header>

                            <div class="flex items-center flex-grow p-4 leading-none sm:p-6">
                                <div class="flex-none">
                                    <div class="mb-3 text-4xl font-semibold">{{ tenancy.balance | money(0) }}</div>

                                    <div
                                        v-if="$gate.role('landlord')"
                                        class="text-sm text-grey-darker"
                                    >
                                        {{ tenancy.credited | money(0) }} paid to date
                                    </div>
                                </div>
                            </div>

                            <component
                                :is="tenancy.payable > 0 ? 'router-link' : 'div'"
                                :to="{ name: $router.fix('dashboard.*.tenancies.charges.create') }"
                                :class="tenancy.payable > 0 ? 'bg-blue text-white' : 'bg-grey-lighter text-grey-dark cursor-not-allowed'"
                                class="block w-full py-3 text-center no-underline rounded-b"
                            >
                                <div class="inline-flex items-center">
                                    Make a payment

                                    <base-svg
                                        :class="tenancy.payable > 0 ? 'fill-primary-white' : 'fill-primary-grey-dark'"
                                        name="icon-cheveron-right"
                                        class="w-6 h-6"
                                    />
                                </div>
                            </component>
                        </div>
                    </grid-layout-item>
                    <grid-layout-item
                        v-if="$gate.role('tenant')"
                        class="mb-6 md:w-2/3"
                    >
                        <div class="flex flex-col h-full bg-white rounded shadow id-smart-payments-card">
                            <header class="flex items-center flex-none h-16 px-4 border-b sm:px-6">
                                <h3 class="flex-grow text-base font-normal">Smart Payments</h3>

                                <span
                                    :class="!!tenancy.auto_payment ? 'bg-green-lighter text-green-darker' : 'bg-red-lighter text-red-darker'"
                                    class="flex-none px-3 py-1 text-sm font-semibold rounded-full"
                                >
                                    {{ !!tenancy.auto_payment ? 'Enabled' : 'Disabled' }}
                                </span>
                            </header>

                            <div class="flex-grow px-4 py-3 text-grey-darker sm:px-6">
                                <template v-if="!!tenancy.auto_payment">
                                    You're currently paying <em class="italic">up to</em> <strong class="font-semibold">{{ tenancy.auto_payment.amount | money(0) }}</strong>
                                    automatically on the <strong class="font-semibold">{{ tenancy.starts_at | moment('Do') }}</strong> of every month with a
                                    <strong class="font-semibold">{{ tenancy.auto_payment.source.brand }}</strong> ending <strong class="font-semibold">{{ tenancy.auto_payment.source.last_four }}</strong>.
                                </template>

                                <template v-else>
                                    Automatic, direct debit-esque rent payments taken the day rent is due. Smart because
                                    we pay <em class="italic">the remaining balance</em>.
                                </template>
                            </div>

                            <div
                                v-if="!!tenancy.auto_payment"
                                class="px-4 py-3 text-center bg-grey-bg sm:px-6"
                            >
                                <auto-payments-delete-button
                                    :sid="sid"
                                    :tenancy="tenancy"
                                    type="button"
                                    class="text-red focus:outline-none"
                                >
                                    Disable Smart Payments
                                </auto-payments-delete-button>
                            </div>

                            <component
                                v-else
                                :is="!(tenancy.archived_at || tenancy.terminated_at) ? 'router-link' : 'div'"
                                :to="{ name: $router.fix('dashboard.*.tenancies.auto-payments.create') }"
                                :class="!(tenancy.archived_at || tenancy.terminated_at) ? 'bg-green text-white' : 'bg-grey-lighter text-grey-dark cursor-not-allowed'"
                                class="block w-full py-3 text-center no-underline rounded-b"
                            >
                                <div class="inline-flex items-center">
                                    Enable Smart Payments

                                    <base-svg
                                        :class="!!tenancy.auto_payment ? 'fill-primary-grey-dark' : 'fill-primary-white'"
                                        name="icon-cheveron-right"
                                        class="w-6 h-6"
                                    />
                                </div>
                            </component>
                        </div>
                    </grid-layout-item>
                    <grid-layout-item
                        class="mb-6"
                        :class="{ 'md:w-2/3' : $gate.role('landlord') }"
                    >
                        <charges-card
                            :tenancy="tenancy.uuid"
                            class="id-charges-card"
                        />
                    </grid-layout-item>
                </grid-layout>

                <tasks-card
                    :data="tenancy.tasks.data"
                    :meta="tenancy.tasks.meta"
                    :can-new="$gate.role('landlord')"
                    :new-to="{
                        name: $router.fix('dashboard.*.tasks.create'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.tasks.index'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    class="mb-6 id-tasks-card"
                />

                <notes-card
                    v-if="$gate.role('landlord')"
                    :note="tenancy.note"
                    :notable="tenancy"
                    class="mb-6"
                />

                <residencies-card
                    v-if="$gate.role('landlord')"
                    :data="tenancy.residencies.data"
                    :meta="tenancy.residencies.meta"
                    :new-to="{
                        name: $router.fix('dashboard.*.residencies.create'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.tenants.index'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    class="mb-6 id-residencies-card"
                    @success="find"
                />

                <rents-card
                    :data="tenancy.rents.data"
                    :meta="tenancy.rents.meta"
                    :more-to="{
                        name: $router.fix('dashboard.*.rents.index'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    class="mb-6 id-rents-card"
                />

                <fees-card
                    :data="tenancy.fees.data"
                    :meta="tenancy.fees.meta"
                    :can-new="$gate.role('landlord') && !(tenancy.archived_at || tenancy.terminated_at)"
                    :new-to="{
                        name: $router.fix('dashboard.*.fees.create'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.fees.index'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    class="mb-6 id-fees-card"
                />

                <documents-card
                    :data="tenancy.documents.data"
                    :meta="tenancy.documents.meta"
                    :can-new="$gate.role('landlord')"
                    :new-to="{
                        name: $router.fix('dashboard.*.documents.create'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.documents.index'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    class="mb-6 id-documents-card"
                />

                <inventories-card
                    :data="tenancy.inventories.data"
                    :meta="tenancy.inventories.meta"
                    :can-new="$gate.role('landlord') && !(tenancy.archived_at || tenancy.terminated_at)"
                    :new-to="{
                        name: $router.fix('dashboard.*.inventories.create'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    :more-to="{
                        name: $router.fix('dashboard.*.inventories.index'),
                        query: {
                            tenancy: tenancy.uuid
                        }
                    }"
                    class="mb-6 id-inventories-card"
                />

                <welcome-modal
                    v-if="$gate.role('tenant')"
                    :tenancy="tenancy"
                />
            </template>
        </div>
    </tenancies-find>
</template>

<script>
    import scroll from '../../../../support/scroll'
    import AutoPaymentsDeleteButton from '../../tenants/tenancies/show/auto-payments-delete-button'
    import BaseTabbed from '../../../../components/base-tabbed'
    import BaseTenancyPeriod from '../../../../components/base-tenancy-period'
    import ChargesCard from '../../../../components/charges-card'
    import ChatsStoreButton from '../../../../components/chats-store-button'
    import DetailsCard from '../../../../components/details-card'
    import DocumentsCard from '../../../../components/documents-card'
    import EditDropdown from '../../landlords/tenancies/show/edit-dropdown'
    import FeesCard from '../../../../components/fees-card'
    import InventoriesCard from '../../../../components/inventories-card'
    import NotesCard from '../../../../components/notes-card'
    import RentsCard from '../../../../components/rents-card'
    import ResidenciesCard from '../../landlords/tenancies/show/residencies-card'
    import TasksCard from '../../../../components/tasks-card'
    import TenanciesFind from '../../../../components/tenancies-find'
    import TenantsCard from '../../../../components/tenants-card'
    import WelcomeModal from '../../tenants/tenancies/show/welcome-modal'

    export default {
        components: {
            AutoPaymentsDeleteButton,
            BaseTabbed,
            BaseTenancyPeriod,
            ChargesCard,
            ChatsStoreButton,
            DetailsCard,
            DocumentsCard,
            EditDropdown,
            FeesCard,
            InventoriesCard,
            NotesCard,
            RentsCard,
            ResidenciesCard,
            TasksCard,
            TenanciesFind,
            TenantsCard,
            WelcomeModal
        },
        beforeRouteLeave (to, from, next) {
            this.$introjs.exit()

            next()
        },
        created () {
            this.$eventBus.$on('the-layout-dashboard:is_sidebar_open', () => {
                this.$introjs.exit()
            })
        },
        methods: {
            introjs () {
                if (this.$gate.role('landlord')) {
                    this.$introjs.start([
                        {
                            intro:
                                '<p class="mb-3">The tenancy details are shown here.</p>' +
                                '<p>More options are available from the <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="inline-block w-5 h-5 icon-dots-horizontal"> <path fill-rule="evenodd" clip-rule="evenodd" d="M5 14C4.46957 14 3.96086 13.7893 3.58579 13.4142C3.21071 13.0391 3 12.5304 3 12C3 11.4696 3.21071 10.9609 3.58579 10.5858C3.96086 10.2107 4.46957 10 5 10C5.53043 10 6.03914 10.2107 6.41421 10.5858C6.78929 10.9609 7 11.4696 7 12C7 12.5304 6.78929 13.0391 6.41421 13.4142C6.03914 13.7893 5.53043 14 5 14ZM12 14C11.4696 14 10.9609 13.7893 10.5858 13.4142C10.2107 13.0391 10 12.5304 10 12C10 11.4696 10.2107 10.9609 10.5858 10.5858C10.9609 10.2107 11.4696 10 12 10C12.5304 10 13.0391 10.2107 13.4142 10.5858C13.7893 10.9609 14 11.4696 14 12C14 12.5304 13.7893 13.0391 13.4142 13.4142C13.0391 13.7893 12.5304 14 12 14ZM19 14C18.4696 14 17.9609 13.7893 17.5858 13.4142C17.2107 13.0391 17 12.5304 17 12C17 11.4696 17.2107 10.9609 17.5858 10.5858C17.9609 10.2107 18.4696 10 19 10C19.5304 10 20.0391 10.2107 20.4142 10.5858C20.7893 10.9609 21 11.4696 21 12C21 12.5304 20.7893 13.0391 20.4142 13.4142C20.0391 13.7893 19.5304 14 19 14Z" class="primary"></path></svg> menu, for example, terminating the tenancy.</p>',
                            element: '.id-details-card',
                            position: 'bottom'
                        },
                        {
                            intro: '<p>You can make offline payments to record payments you\'ve received outside of Rentora, for example, cash in hand payments.</p>',
                            element: '.id-balance-card'
                        },
                        {
                            intro: '<p>Recent payments towards this tenancy are listed here.</p>',
                            element: '.id-charges-card'
                        },
                        {
                            intro:
                                '<p>Tasks will keep you on track with what to do next with this tenancy.</p>' +
                                '<p class="text-sm">p.s. You can click on tasks.</p>',
                            element: '.id-tasks-card'
                        },
                        {
                            intro:
                                '<p class="mb-3">The tenants you\'ve added to this tenancy are here.</p>' +
                                '<p>You can invite them to join Rentora to make sharing documents, inventories, messages, etc much easier.</p>',
                            element: '.id-residencies-card'
                        },
                        {
                            intro: '<p>Rents are shown in order of importance, but most of the time you\'ll see due then upcoming rents.</p>',
                            element: '.id-rents-card'
                        },
                        {
                            intro:
                                '<p class="mb-3">You can add fees to a tenancy to request money from your tenant for miscellaneous items, for example, charging for replacement keys.</p>' +
                                '<p>Tenants pay fees just like they would rent.</p>',
                            element: '.id-fees-card'
                        },
                        {
                            intro:
                                '<p class="mb-3">Upload documents related to this tenancy for safe keeping, for example the tenancy agreement.</p>' +
                                '<p class="text-sm">Tip: You can easily opt to share specific documents with your tenants.</p>',
                            element: '.id-documents-card'
                        },
                        {
                            intro: '<p>Quickly perform inventories that are shared with your tenants. We safely store your photos in high quality so you can refer to them later.</p>',
                            element: '.id-inventories-card'
                        }
                    ])
                        .oncomplete(() => {
                            scroll.toTop()
                        })
                } else {
                    this.$introjs.start([
                        {
                            intro: '<p>The tenancy details are shown here - and you have the ability to message your landlord.</p>',
                            element: '.id-details-card',
                            position: 'bottom'
                        },
                        {
                            intro:
                                '<p class="mb-3">You can make a single payment towards your tenancy balance at any time.</p>' +
                                '<p class="text-sm">Tip: You can make smaller contributions, for example, pay weekly if that better suits your budget.</p>',
                            element: '.id-balance-card'
                        },
                        {
                            intro:
                                '<p class="mb-3">Ensure your rent is automatically paid on time, while still allowing for single payments.</p>' +
                                '<p class="text-sm">Tip: We recommend enabling smart payments to ensure your rent is always paid on time - even if you plan on taking advantage of budgeting with single payments.</p>',
                            element: '.id-smart-payments-card'
                        },
                        {
                            intro: '<p>Recent payments towards this tenancy are listed here.</p>',
                            element: '.id-charges-card'
                        },
                        {
                            intro:
                                '<p>Tasks will keep you on track with what to do next with this tenancy.</p>' +
                                '<p class="text-sm">p.s. You can click on tasks.</p>',
                            element: '.id-tasks-card'
                        },
                        {
                            intro:
                                '<p class="mb-3">Rents are shown in order of importance, but most of the time you\'ll see due then upcoming rents.</p>' +
                                '<p class="text-sm">Tip: You can make payments towards any rent, at any time, for example, get ahead on next month\'s rent, or pay off December’s rent throughout the year and have a rent-free christmas.</p>',
                            element: '.id-rents-card'
                        },
                        {
                            intro: '<p>Your landlord may require you pay for something other than rent, for example, replacement keys.</p>',
                            element: '.id-fees-card'
                        },
                        {
                            intro: '<p class="mb-3">Documents shared by your landlord are listed here, available for viewing any time.</p>',
                            element: '.id-documents-card'
                        },
                        {
                            intro: '<p>Similar to Documents - the inventory your landlord performed is shown here for you to view at any time.</p>',
                            element: '.id-inventories-card'
                        }
                    ])
                        .oncomplete(() => {
                            scroll.toTop()
                        })
                }
            }
        }
    }
</script>
